import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ReadTransaction } from "replicache";
import { useAuth } from "../auth/context";
import { startUploader } from "../attachments/uploader";
import { createReplicache, type AppReplicache } from "./instance";

// We hand out the live Replicache instance via context.  It's created on
// sign-in and torn down on sign-out, scoped per-user (so two users on the
// same device get isolated IndexedDB stores).

const RepCtx = createContext<AppReplicache | null>(null);

export function ReplicacheProvider({ children }: { children: ReactNode }) {
  const { state } = useAuth();

  // Stash the latest access token in a ref so the Replicache puller/pusher
  // pick up rotations immediately without re-creating the instance.
  const tokenRef = useRef<string | null>(null);
  const userId =
    state.status === "authenticated" || state.status === "offline"
      ? state.status === "authenticated"
        ? state.user.id
        : state.user?.id ?? null
      : null;
  if (state.status === "authenticated" || state.status === "offline") {
    tokenRef.current = state.session.access_token;
  } else {
    tokenRef.current = null;
  }

  const [rep, setRep] = useState<AppReplicache | null>(null);

  useEffect(() => {
    if (!userId) {
      setRep(null);
      return;
    }
    const instance = createReplicache({
      userId,
      getAccessToken: () => tokenRef.current,
    });
    const uploader = startUploader(instance, () => tokenRef.current);
    setRep(instance);
    return () => {
      uploader.stop();
      void instance.close();
      setRep(null);
    };
  }, [userId]);

  return <RepCtx.Provider value={rep}>{children}</RepCtx.Provider>;
}

export function useReplicache(): AppReplicache | null {
  return useContext(RepCtx);
}

// Tiny re-implementation of replicache-react's useSubscribe — that package
// requires React 19; we're on 18.  Re-subscribes whenever rep or deps change;
// the query closure is captured at that point.
export function useSubscribe<T>(
  rep: AppReplicache | null,
  query: (tx: ReadTransaction) => Promise<T>,
  defaultValue: T,
  deps: ReadonlyArray<unknown> = [],
): T {
  const [data, setData] = useState<T>(defaultValue);
  useEffect(() => {
    if (!rep) {
      setData(defaultValue);
      return;
    }
    const cancel = rep.subscribe(query, { onData: setData });
    return () => cancel();
    // The query closure is what changes; deps are how the caller signals it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rep, ...deps]);
  return data;
}
